import React, { useState, useEffect } from "react";
import { Card, Form, Button, Table, Badge, Spinner, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../services/api";

const Rekapitulasi = () => {
  const [tglAwal, setTglAwal] = useState("");
  const [tglAkhir, setTglAkhir] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // Helper function to format date as yyyy-mm-dd in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Set default date range (start of current year to today)
  useEffect(() => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    setTglAwal(formatDateLocal(startOfYear));
    setTglAkhir(formatDateLocal(today));
  }, []);

  const fetchData = async () => {
    if (!tglAwal || !tglAkhir) {
      setError("Tanggal awal dan akhir harus diisi");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_URL}/reguler/rekap-reguler?tgl_awal=${tglAwal}&tgl_akhir=${tglAkhir}`
      );

      if (response.data.success) {
        setData(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        setError(response.data.message || "Gagal mengambil data");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.response?.data?.message || "Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for each column
  const calculateTotals = () => {
    const totals = {
      draft_pu: 0,
      submitted_pu: 0,
      dikembalikan: 0,
      verified: 0,
      dikirim_ke_lph: 0,
      penetapan_biaya: 0,
      pembayaran: 0,
      proses_di_lph: 0,
      post_audit_lph: 0,
      dikirim_ke_komisi_fatwa: 0,
      diterima_komisi_fatwa: 0,
      proses_sidang_fatwa: 0,
      ditetapkan_halal: 0,
      ditolak: 0,
      dibatalkan: 0,
      terbit_sh: 0,
      total: 0,
    };

    data.forEach((item) => {
      Object.keys(totals).forEach((key) => {
        totals[key] += item[key] || 0;
      });
    });

    return totals;
  };

  const totals = calculateTotals();

  // Status column definitions for table
  const statusColumns = [
    { key: "draft_pu", label: "Draft PU", variant: "secondary" },
    { key: "submitted_pu", label: "Submitted", variant: "info" },
    { key: "dikembalikan", label: "Dikembalikan", variant: "warning" },
    { key: "verified", label: "Verified", variant: "primary" },
    { key: "dikirim_ke_lph", label: "Dikirim ke LPH", variant: "info" },
    { key: "penetapan_biaya", label: "Penetapan Biaya", variant: "warning" },
    { key: "pembayaran", label: "Pembayaran", variant: "success" },
    { key: "proses_di_lph", label: "Proses di LPH", variant: "primary" },
    { key: "post_audit_lph", label: "Post Audit", variant: "info" },
    { key: "dikirim_ke_komisi_fatwa", label: "Dikirim ke Fatwa", variant: "info" },
    { key: "diterima_komisi_fatwa", label: "Diterima Fatwa", variant: "primary" },
    { key: "proses_sidang_fatwa", label: "Proses Sidang", variant: "warning" },
    { key: "ditetapkan_halal", label: "Ditetapkan Halal", variant: "success" },
    { key: "ditolak", label: "Ditolak", variant: "danger" },
    { key: "dibatalkan", label: "Dibatalkan", variant: "danger" },
    { key: "terbit_sh", label: "Terbit SH", variant: "success" },
  ];

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-4">
        <i className="fas fa-chart-bar me-2"></i>
        Rekapitulasi Pengajuan Reguler
      </h4>

      {/* Filter Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Tanggal Awal</Form.Label>
                <Form.Control
                  type="date"
                  value={tglAwal}
                  onChange={(e) => setTglAwal(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Tanggal Akhir</Form.Label>
                <Form.Control
                  type="date"
                  value={tglAkhir}
                  onChange={(e) => setTglAkhir(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button
                variant="primary"
                onClick={fetchData}
                disabled={loading}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Memuat...
                  </>
                ) : (
                  <>
                    <i className="fas fa-filter me-2"></i>
                    Tampilkan Data
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <span>
            <i className="fas fa-table me-2"></i>
            Data Rekapitulasi per Kota/Kabupaten
          </span>
          <Badge bg="light" text="dark">
            {data.length} Lokasi
          </Badge>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: "600px" }}>
            <Table striped bordered hover size="sm" className="mb-0" style={{ fontSize: "0.75rem" }}>
              <thead className="table-dark sticky-top">
                <tr>
                  <th style={{ minWidth: "150px" }}>Provinsi</th>
                  <th style={{ minWidth: "180px" }}>Kota/Kabupaten</th>
                  {statusColumns.map((col) => (
                    <th key={col.key} style={{ minWidth: "80px", textAlign: "center" }}>
                      {col.label}
                    </th>
                  ))}
                  <th style={{ minWidth: "70px", textAlign: "center" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={18} className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted">Memuat data...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={18} className="text-center py-4 text-muted">
                      <i className="fas fa-inbox fa-2x mb-2"></i>
                      <p>Belum ada data untuk ditampilkan</p>
                      <p className="small">Silakan pilih rentang tanggal dan klik "Tampilkan Data"</p>
                    </td>
                  </tr>
                ) : (
                  <>
                    {data.map((item, index) => (
                      <tr key={index}>
                        <td className="fw-medium">{item.prov_pu}</td>
                        <td>{item.kota_pu}</td>
                        {statusColumns.map((col) => (
                          <td key={col.key} className="text-center">
                            {item[col.key] > 0 ? (
                              <Badge bg={col.variant}>{item[col.key]}</Badge>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        ))}
                        <td className="text-center fw-bold">{item.total}</td>
                      </tr>
                    ))}
                    {/* Total Row */}
                    <tr className="table-dark fw-bold">
                      <td colSpan={2} className="text-end">TOTAL</td>
                      {statusColumns.map((col) => (
                        <td key={col.key} className="text-center">
                          {totals[col.key] > 0 ? totals[col.key] : "-"}
                        </td>
                      ))}
                      <td className="text-center">{totals.total}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

    </div>
  );
};

export default Rekapitulasi;
